/**
 * GenerateApiKeyModal — thin wrapper around @insio/ui-toolkit module.
 * Integrates GraphQL mutation and offers the roles a key may hold.
 */
import { useMutation } from '@apollo/client/react';
import {
  type GenerateApiKeyFormData,
  GenerateApiKeyModal as GenerateApiKeyModalBase
} from '@insio/ui-toolkit';
import { type ApiKeyCreated, CreateApiKeyDocument } from '@/graphql';
import { KEY_ASSIGNABLE_ROLES } from '@/features/admin/constants/roles';
import { useRoleLabelOptions } from '@/features/admin/hooks/use-role-label-options';

interface GenerateApiKeyModalProps {
  onClose: () => void;
  onSuccess: (key: ApiKeyCreated) => void;
}

export function GenerateApiKeyModal({ onClose, onSuccess }: GenerateApiKeyModalProps) {
  const roleOptions = useRoleLabelOptions(KEY_ASSIGNABLE_ROLES);
  const [createApiKey, { loading, error }] = useMutation(CreateApiKeyDocument);

  const handleSubmit = async (data: GenerateApiKeyFormData) => {
    const result = await createApiKey({
      variables: {
        input: {
          name: data.name,
          description: data.description || undefined,
          rateLimitPerMin: data.rateLimitPerMin,
          roles: KEY_ASSIGNABLE_ROLES.filter(role => data.roles.includes(role))
        }
      }
    });
    if (result.data?.createApiKey) {
      onSuccess(result.data.createApiKey);
    }
  };

  return (
    <GenerateApiKeyModalBase
      open={true}
      onClose={onClose}
      roleOptions={roleOptions}
      onSubmit={handleSubmit}
      loading={loading}
      error={error?.message}
    />
  );
}
